'use client';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@shared/components/ui/shadcn/dialog';
import { cn } from '@shared/utils/utils';
import { forwardRef, useCallback, useImperativeHandle, useRef, type CSSProperties } from 'react';
import { generateBaseTestId, resolveWidthClass, useModal } from './hooks';
import type { ModalCenterProps, ModalCenterRef } from './types';
import './ModalCenter.css';

/**
 * Componente ModalCenter - Modal centrado con diferentes tipos y tamaños
 *
 * Modal reutilizable con soporte para diferentes tipos (success, error, warning, info, default),
 * tamaños configurables y referencia imperativa para control externo.
 *
 * Características:
 * - Tipos visuales predefinidos
 * - Tamaños configurables (default, lg, xl, custom)
 * - Referencia imperativa para control programático
 * - Test IDs automáticos basados en título
 * - Callback de cierre personalizable
 * - Clases CSS personalizables
 *
 * @param children - Contenido del modal
 * @param title - Título opcional del modal
 * @param icon - Icono opcional junto al título
 * @param type - Tipo visual del modal
 * @param onCloseModal - Callback ejecutado al cerrar
 * @param className - Clases CSS adicionales para el contenedor
 * @param classNameBody - Clases CSS adicionales para el cuerpo
 * @param width - Ancho del modal (default, lg, xl, o número)
 * @param data-testid - Test ID personalizado
 * @param ref - Referencia imperativa para control externo
 */
const ModalCenter = forwardRef<ModalCenterRef, ModalCenterProps>(
  (
    {
      children,
      title,
      icon,
      type = 'default',
      onCloseModal,
      width = 'default',
      maxHeight,
      'data-testid': testIdProp,
      className,
      classNameBody,
      footer,
      fullScreenMobile = false,
      fullScreen = false,
      preventClose = false,
      preventDismiss = false,
      onOpenAutoFocus,
    },
    ref,
  ) => {
    // ============================================================================
    // HOOKS Y ESTADO
    // ============================================================================

    const { isOpen, openModal, closeModal } = useModal(onCloseModal);
    const returnFocusRef = useRef<HTMLElement | null>(null);
    const openWithFocus = useCallback(() => {
      if (!isOpen) {
        returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      }
      openModal();
    }, [isOpen, openModal]);

    // ============================================================================
    // REFERENCIA IMPERATIVA
    // ============================================================================

    useImperativeHandle(
      ref,
      () => ({
        openModal: openWithFocus,
        closeModal,
      }),
      [openWithFocus, closeModal],
    );

    // ============================================================================
    // VALORES COMPUTADOS
    // ============================================================================

    // Si title es ReactNode, extraer el texto para el testId
    const getTitleText = (title: React.ReactNode): string => {
      if (typeof title === 'string') return title;
      if (typeof title === 'number') return title.toString();
      // Si es un elemento React con props
      if (
        title &&
        typeof title === 'object' &&
        'props' in title &&
        (title as any).props &&
        Object.prototype.hasOwnProperty.call((title as any).props, 'children')
      ) {
        return getTitleText((title as any).props.children);
      }
      return '';
    };
    const baseTestId = generateBaseTestId(testIdProp, getTitleText(title), type);
    const widthClass = resolveWidthClass(width);
    const resolvedMaxHeight = typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight;
    const safeViewportMaxHeight = 'calc(100dvh - 2rem)';
    const contentMaxHeight = resolvedMaxHeight
      ? `min(${resolvedMaxHeight}, ${safeViewportMaxHeight})`
      : safeViewportMaxHeight;
    const useFullScreenMobileDesktopMaxHeight = fullScreenMobile && Boolean(resolvedMaxHeight);

    // ============================================================================
    // HANDLERS
    // ============================================================================

    const handleOpenChange = (open: boolean) => {
      if (!open && (preventClose || preventDismiss)) {
        return;
      }
      if (!open) {
        closeModal();
      }
    };

    // ============================================================================
    // RENDER
    // ============================================================================

    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange} data-testid={baseTestId}>
        <DialogContent
          hideCloseButton={preventClose}
          onCloseButtonClick={preventDismiss && !preventClose ? closeModal : undefined}
          onOpenAutoFocus={onOpenAutoFocus}
          onPointerDownOutside={preventClose || preventDismiss ? (event) => event.preventDefault() : undefined}
          onInteractOutside={preventClose || preventDismiss ? (event) => event.preventDefault() : undefined}
          onEscapeKeyDown={preventClose || preventDismiss ? (event) => event.preventDefault() : undefined}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            const opener = returnFocusRef.current;
            // Operational consumers may explicitly focus their scanner after closing.
            // Restore the opener only if that consumer has left focus on the page.
            setTimeout(() => {
              if (document.activeElement === document.body && opener?.isConnected && !opener.matches(':disabled')) {
                opener.focus();
              }
            }, 0);
          }}
          className={cn(
            'p-0 bg-body-bg border-card-border gap-1 grid',
            footer ? 'grid-rows-[auto_1fr_auto]' : 'grid-rows-[auto_1fr]',
            !fullScreen && !fullScreenMobile && 'w-[calc(100%-20px)] max-w-none',
            !fullScreen && widthClass,
            !fullScreen && !fullScreenMobile && 'max-h-[calc(100dvh-2rem)]',
            fullScreenMobile &&
              (useFullScreenMobileDesktopMaxHeight
                ? 'modal-fullscreen-mobile modal-desktop-max-height w-screen max-w-full rounded-none'
                : 'modal-fullscreen-mobile h-screen sm:h-auto max-h-screen w-screen max-w-full rounded-none'),
            fullScreen && [
              '!left-0 !top-0 !right-0 !bottom-0 !translate-x-0 !translate-y-0',
              '!w-screen !h-screen !max-w-none !max-h-none',
              'rounded-none',
            ],
            className,
          )}
          style={
            fullScreen
              ? {
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '100vw',
                  height: '100vh',
                  maxWidth: 'none',
                  maxHeight: 'none',
                  transform: 'none',
                }
              : useFullScreenMobileDesktopMaxHeight
                ? ({ '--modal-desktop-max-height': contentMaxHeight } as CSSProperties)
                : fullScreenMobile
                  ? undefined
                  : { maxHeight: contentMaxHeight }
          }
          data-testid={`${baseTestId}-content`}
          aria-describedby=""
        >
          {title && (
            <DialogHeader
              className="p-4 pb-1 flex items-start justify-between overflow-hidden"
              data-testid={`${baseTestId}-header`}
            >
              <DialogTitle
                className="text-foreground border-border transition-colors text-md flex items-center gap-2 flex-1 min-w-0 overflow-hidden w-[calc(100%-22px)]"
                data-testid={`${baseTestId}-title`}
              >
                {icon && icon}
                <span className="block w-full overflow-hidden text-ellipsis whitespace-nowrap">{title}</span>
              </DialogTitle>
            </DialogHeader>
          )}

          <div className={cn('p-4 pt-0 overflow-auto', classNameBody)} data-testid={`${baseTestId}-body`}>
            {children}
          </div>

          {footer && (
            <div className="p-4 border-t border-card-border" data-testid={`${baseTestId}-footer`}>
              {footer}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  },
);

ModalCenter.displayName = 'ModalCenter';

export default ModalCenter;
